package com.rosario.juridico.ui.screens

import com.rosario.juridico.ui.components.CajaResultadoAccionable
import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import com.rosario.juridico.ui.components.WebScrollableBox
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.domain.*
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.platform.agregarEventoCalendario
import com.rosario.juridico.platform.localDateToMillis
import com.rosario.juridico.ui.theme.Gold400
import com.rosario.juridico.ui.components.JuridicoDatePickerDialogKMP
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.todayIn
import kotlinx.datetime.until

// ─────────────────────────────────────────────────────────────────────────────
// MODELO DE RESULTADO
// ─────────────────────────────────────────────────────────────────────────────

data class ResultadoPlazo(
    val record:     PlazoRecord,
    val fechaNotif: LocalDate,
    val fechaVenc:  LocalDate?,
    val tipoComputo: String
)

// ─────────────────────────────────────────────────────────────────────────────
// LÓGICA DE CÁLCULO
// ─────────────────────────────────────────────────────────────────────────────

private fun esDiaHabil(fecha: LocalDate, feriados: Set<LocalDate>): Boolean {
    val dow = fecha.dayOfWeek
    if (dow == kotlinx.datetime.DayOfWeek.SATURDAY || dow == kotlinx.datetime.DayOfWeek.SUNDAY) return false
    if (fecha in feriados) return false
    return true
}

fun sumarDiasHabiles(desde: LocalDate, cantidad: Int, feriados: Set<LocalDate>): LocalDate {
    var fecha    = desde
    var contados = 0
    while (contados < cantidad) {
        fecha = fecha.plus(1, DateTimeUnit.DAY)
        if (esDiaHabil(fecha, feriados)) contados++
    }
    return fecha
}

fun sumarDiasCorridos(desde: LocalDate, cantidad: Int): LocalDate =
    desde.plus(cantidad, DateTimeUnit.DAY)

fun calcularVencimientoRecord(inicio: LocalDate, record: PlazoRecord, feriados: Set<LocalDate>): LocalDate? {
    val valorNum = record.valor.toIntOrNull() ?: 0
    return when (record.tipoComputo.uppercase()) {
        "HABIL" -> sumarDiasHabiles(inicio, valorNum, feriados)
        "CORRIDO" -> inicio.plus(valorNum, DateTimeUnit.DAY)
        "MES" -> inicio.plus(valorNum, DateTimeUnit.MONTH)
        "AÑO" -> inicio.plus(valorNum, DateTimeUnit.YEAR)
        "VARIABLE" -> null
        else -> null
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// PANTALLA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PantallaCalculadoraVencimientos(navController: NavController) {
    var feriados: Set<LocalDate> by remember { mutableStateOf(emptySet()) }
    var plazosBase by remember { mutableStateOf<List<PlazoRecord>>(emptyList()) }
    var busqueda by remember { mutableStateOf("") }
    
    LaunchedEffect(Unit) { 
        feriados = cargarFeriados() 
        plazosBase = PlazosRepository.getPlazos()
    }

    var fechaSeleccionada: LocalDate? by remember { mutableStateOf(null) }
    var recordElegido: PlazoRecord? by remember { mutableStateOf(null) }
    var resultado: ResultadoPlazo? by remember { mutableStateOf(null) }
    
    var resultVisible: Boolean by remember { mutableStateOf(false) }
    var showDatePicker: Boolean by remember { mutableStateOf(false) }

    val scrollState = rememberScrollState()

    val resultadosFiltrados = remember(busqueda, plazosBase) {
        val q = busqueda.normalized().trim()
        if (q.isEmpty()) {
            plazosBase
        } else {
            plazosBase.filter { 
                it.categoria.normalized().contains(q, true) ||
                it.descripcion.normalized().contains(q, true) ||
                it.articulo.normalized().contains(q, true) ||
                it.observaciones.normalized().contains(q, true)
            }
        }
    }

    // --- EFECTO DE RESULTADO CON DELAY ---
    LaunchedEffect(resultado) {
        if (resultado != null) {
            delay(400)
            resultVisible = true
            delay(500)
            scrollState.animateScrollTo(scrollState.maxValue)
        } else {
            resultVisible = false
        }
    }

    fun limpiar() {
        fechaSeleccionada = null
        recordElegido = null
        resultado = null
        busqueda = ""
    }

    if (showDatePicker) {
        JuridicoDatePickerDialogKMP(
            onDateSelected = { fechaSeleccionada = it; resultado = null },
            onDismiss      = { showDatePicker = false }
        )
    }

    Box(modifier = Modifier.fillMaxSize()) {
        WebScrollableBox(scrollState = scrollState, modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .imePadding()
                    .verticalScroll(scrollState)
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
            UsageCounterIndicator()
            PremiumHeader(
                icono     = Icons.Default.CalendarMonth,
                titulo    = "Vencimientos Procesales",
                subtitulo = "CPCC Santa Fe — Plazos hábiles e improrrogables"
            )

            // ── Búsqueda de plazo ─────────────────────────────────────────────
            OutlinedTextField(
                value = busqueda,
                onValueChange = { busqueda = it },
                label = { Text("Buscar plazo...") },
                placeholder = { Text("Ej: demanda, apelación, 2560...") },
                leadingIcon = { Icon(Icons.Default.Search, null) },
                trailingIcon = {
                    if (busqueda.isNotEmpty()) {
                        IconButton(onClick = { busqueda = "" }) {
                            Icon(Icons.Default.Clear, null)
                        }
                    }
                },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                singleLine = true
            )

            // ── Selector de plazo (si hay búsqueda o está vacío) ─────────────
            var actoExpand by remember { mutableStateOf(false) }
            ExposedDropdownMenuBox(expanded = actoExpand, onExpandedChange = { actoExpand = it }) {
                OutlinedTextField(
                    value         = (recordElegido?.descripcion ?: "Seleccioná el plazo"),
                    onValueChange = {},
                    readOnly      = true,
                    label         = { Text("Plazo seleccionado") },
                    trailingIcon  = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = actoExpand) },
                    modifier      = Modifier.fillMaxWidth().menuAnchor(),
                    shape         = RoundedCornerShape(12.dp)
                )
                ExposedDropdownMenu(
                    expanded = actoExpand, 
                    onDismissRequest = { actoExpand = false },
                    modifier = Modifier.heightIn(max = 400.dp)
                ) {
                    resultadosFiltrados.take(50).forEach { record ->
                        DropdownMenuItem(
                            leadingIcon = {
                                Icon(iconoCategoria(record.categoria), null, modifier = Modifier.size(20.dp), tint = MaterialTheme.colorScheme.primary)
                            },
                            text = {
                                Column {
                                    Text(record.descripcion, style = MaterialTheme.typography.bodyMedium)
                                    Text("${record.categoria} • ${record.valor} ${record.tipoComputo}", 
                                        style = MaterialTheme.typography.labelSmall, 
                                        color = MaterialTheme.colorScheme.primary)
                                }
                            },
                            onClick = { 
                                recordElegido = record
                                resultado = null
                                actoExpand = false
                                busqueda = "" // Limpiar búsqueda al elegir
                            }
                        )
                    }
                }
            }

            // ── Selector de fecha ─────────────────────────────────────────────
            Box(modifier = Modifier.fillMaxWidth()) {
                OutlinedTextField(
                    value         = (fechaSeleccionada?.toTexto() ?: ""),
                    onValueChange = {},
                    readOnly      = true,
                    label         = { Text("Fecha de notificación") },
                    placeholder   = { Text("Seleccioná la fecha") },
                    leadingIcon   = { Icon(Icons.Default.CalendarToday, null, modifier = Modifier.size(20.dp)) },
                    modifier      = Modifier.fillMaxWidth(),
                    shape         = RoundedCornerShape(12.dp),
                    singleLine    = true
                )
                Box(modifier = Modifier.matchParentSize().alpha(0f).clickable { showDatePicker = true })
            }

            // ── Botones ──────────────────────────────────
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedButton(
                    onClick  = { limpiar() },
                    modifier = Modifier.weight(0.35f).height(52.dp),
                    shape    = RoundedCornerShape(12.dp),
                    colors   = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.error)
                ) {
                    Icon(Icons.Default.DeleteSweep, null, Modifier.size(18.dp))
                    Spacer(Modifier.width(4.dp))
                    Text("Limpiar")
                }
                CalculatorActionLock(
                    navController = navController,
                    onAction = {
                        val record = recordElegido ?: return@CalculatorActionLock
                        val fecha = fechaSeleccionada ?: return@CalculatorActionLock
                        val fv = calcularVencimientoRecord(fecha, record, feriados)
                        resultado = ResultadoPlazo(record, fecha, fv, record.tipoComputo)
                    }
                ) { onClick ->
                    Button(
                        onClick = onClick,
                        enabled  = fechaSeleccionada != null && recordElegido != null,
                        modifier = Modifier.weight(0.65f).height(52.dp),
                        shape    = RoundedCornerShape(12.dp)
                    ) {
                        Icon(Icons.Default.Calculate, null, Modifier.size(18.dp))
                        Spacer(Modifier.width(8.dp))
                        Text("Calcular", fontWeight = FontWeight.SemiBold)
                    }
                }
            }

            AnimatedVisibility(visible = resultVisible, enter = fadeIn() + expandVertically(), exit = fadeOut() + shrinkVertically()) {
                resultado?.let { res -> ResultadoVencimientoCard(res) }
            }

            Spacer(Modifier.height(8.dp))
            SeccionLabelVencimientos("Reglas Generales")
            ReglasGeneralesCard()
            Spacer(Modifier.height(24.dp))
        }
    }

        OnboardingTooltip(
            key = "onboarding_vencimientos_cpcc",
            mensaje = "Calculá plazos procesales y sustanciales. Buscá por nombre o artículo y seleccioná la fecha de notificación.",
            modifier = Modifier.align(Alignment.TopCenter).padding(top = 8.dp)
        )
    }
}

@Composable
fun ReglasGeneralesCard() {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(10.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text("• Plazos perentorios e improrrogables.", style = MaterialTheme.typography.bodySmall)
            Text("• Comienzan desde la notificación.", style = MaterialTheme.typography.bodySmall)
            Text("• Hábiles: No se cuenta el día de notificación ni inhábiles.", style = MaterialTheme.typography.bodySmall)
            Text("• Vencen automáticamente sin declaración judicial.", style = MaterialTheme.typography.bodySmall)
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE: CARD DE RESULTADO
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun ResultadoVencimientoCard(res: ResultadoPlazo) {
    val hoy = Clock.System.todayIn(TimeZone.currentSystemDefault())
    val vencimientoEfectivo = res.fechaVenc
    val diasRestan = if (vencimientoEfectivo != null) hoy.until(vencimientoEfectivo, DateTimeUnit.DAY) else 0
    val vencido = if (vencimientoEfectivo != null) diasRestan < 0 else false

    val colorAlerta = when {
        res.record.tipoComputo.uppercase() == "VARIABLE" -> Gold400
        vencido -> MaterialTheme.colorScheme.error
        diasRestan <= 3 -> MaterialTheme.colorScheme.error.copy(alpha = 0.8f)
        diasRestan <= 7 -> Gold400
        else -> MaterialTheme.colorScheme.primary
    }

    val textoCopia = """
        RESUMEN VENCIMIENTO PROCESAL
        Acto: ${res.record.descripcion}
        Categoría: ${res.record.categoria}
        Fecha Notificación: ${res.fechaNotif.toTexto()}
        Cómputo: ${res.record.valor} ${res.tipoComputo.uppercase()}
        -------------------------------------------
        VENCIMIENTO: ${vencimientoEfectivo?.toTexto() ?: "A determinar judicialmente"}
        -------------------------------------------
        Calculado con www.juridicorosario.com
    """.trimIndent()

    CajaResultadoAccionable(
        titulo = "Resultado del Cómputo",
        textoParaCompartir = textoCopia,
        colorFondo = if (vencido) MaterialTheme.colorScheme.errorContainer else MaterialTheme.colorScheme.surface
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            FilaResultado("Categoría", res.record.categoria)
            FilaResultado("Descripción", res.record.descripcion)
            if (res.record.articulo.isNotEmpty()) {
                FilaResultado("Artículo", res.record.articulo)
            }
            if (res.record.observaciones.isNotEmpty()) {
                FilaResultado("Observaciones", res.record.observaciones)
            }
            FilaResultado("Tipo de cómputo", res.tipoComputo.uppercase())
            FilaResultado("Fecha inicial", res.fechaNotif.toTexto())

            if (vencimientoEfectivo != null) {
                FilaResultado("Vencimiento", res.fechaVenc?.toTexto() ?: "-", valorDestacado = true)

                Surface(shape = RoundedCornerShape(8.dp), color = colorAlerta.copy(alpha = 0.12f), modifier = Modifier.fillMaxWidth()) {
                    Row(modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(if (vencido) Icons.Default.Warning else Icons.Default.Alarm, null, tint = colorAlerta, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(8.dp))
                        Text(
                            text = when {
                                vencido -> "⚠️ Plazo vencido hace ${-diasRestan} día(s)"
                                diasRestan == 0 -> "⚠️ Vence HOY"
                                diasRestan == 1 -> "⚠️ Vence MAÑANA"
                                else -> "Restan $diasRestan días corridos"
                            },
                            style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.SemiBold, color = colorAlerta
                        )
                    }
                }

                Button(
                    onClick = {
                        val titulo = "Vence: ${res.record.descripcion}"
                        val desc = "Notificación: ${res.fechaNotif.toTexto()}\nPlazo: ${res.record.valor} ${res.record.tipoComputo}"
                        agregarEventoCalendario(titulo, localDateToMillis(vencimientoEfectivo), desc)
                    },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondary)
                ) {
                    Icon(Icons.Default.Event, null, Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Agendar en Calendario", fontWeight = FontWeight.SemiBold)
                }
            } else {
                Surface(shape = RoundedCornerShape(8.dp), color = colorAlerta.copy(alpha = 0.12f), modifier = Modifier.fillMaxWidth()) {
                    Row(modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Info, null, tint = colorAlerta, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(8.dp))
                        Text(
                            "Este plazo depende de resolución judicial o de circunstancias procesales específicas.",
                            style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.SemiBold, color = colorAlerta
                        )
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTES AUXILIARES
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun FilaResultado(label: String, valor: String, valorDestacado: Boolean = false) {
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
        Text(label, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.weight(0.45f))
        Text(valor, style = MaterialTheme.typography.bodySmall, fontWeight = if (valorDestacado) FontWeight.Bold else FontWeight.Normal, color = if (valorDestacado) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurface, modifier = Modifier.weight(0.55f))
    }
}

@Composable
private fun SeccionLabelVencimientos(texto: String) {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(top = 12.dp, bottom = 4.dp).fillMaxWidth()) {
        Box(modifier = Modifier.width(4.dp).height(18.dp).background(Gold400, RoundedCornerShape(2.dp)))
        Spacer(Modifier.width(8.dp))
        Text(texto, style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.onBackground, letterSpacing = 0.8.sp)
        Spacer(Modifier.width(8.dp))
        HorizontalDivider(modifier = Modifier.weight(1f), thickness = 1.dp, color = MaterialTheme.colorScheme.outlineVariant)
    }
}

private fun iconoCategoria(categoria: String): ImageVector = when (categoria) {
    "Demanda y Contestación" -> Icons.Default.Gavel
    "Excepciones Previas"    -> Icons.Default.Shield
    "Prueba"                 -> Icons.Default.Search
    "Cautelares"             -> Icons.Default.Lock
    "Peritos"                -> Icons.Default.Person
    "Recursos"               -> Icons.Default.Upload
    "Sentencia"              -> Icons.Default.AccountBalance
    "Incidentes"             -> Icons.Default.Warning
    "Ejecución de Sentencia" -> Icons.Default.PlayArrow
    "Sucesiones"             -> Icons.Default.FamilyRestroom
    "Honorarios"             -> Icons.Default.AttachMoney
    "Otros"                  -> Icons.Default.MoreHoriz
    "REGLAS GENERALES"       -> Icons.Default.Gavel
    "DEMANDA Y CONTESTACION" -> Icons.Default.Gavel
    "RECONVENCION"           -> Icons.Default.SyncAlt
    "EXCEPCIONES PREVIAS"    -> Icons.Default.Shield
    "PRUEBA"                 -> Icons.Default.Search
    "MEDIDAS PREPARATORIAS Y CAUTELARES" -> Icons.Default.Lock
    "PERITOS"                -> Icons.Default.Person
    "PERICIA"                -> Icons.Default.Person
    "CADUCIDAD DE INSTANCIA" -> Icons.Default.Timer
    "NULIDADES"              -> Icons.Default.Block
    "RECUSACION"             -> Icons.Default.RemoveCircle
    "DOCUMENTAL"             -> Icons.Default.Description
    "PRESCRIPCION"           -> Icons.Default.History
    "FAMILIA"                -> Icons.Default.FamilyRestroom
    "SUCESIONES"             -> Icons.Default.FamilyRestroom
    "LOCACION"               -> Icons.Default.Home
    "ALIMENTOS"              -> Icons.Default.Restaurant
    else                     -> Icons.Default.CalendarMonth
}
